import React, {useState, useEffect, useCallback, useMemo} from "react";
import {Box, Stack, Typography} from "@mui/material";
import clsx from "clsx";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";

import styles from "./Adults.module.css";
import {CustomTabs, Loader, MovieCard, VerificationCode} from "../../components";
import useResize from "../../hooks/useResize";
import {
    IDataResponse,
    useFetchCategoriesRequestQuery,
    useFetchRelatedContentMutation,
    useLazyGetPageContentQuery
} from "../../api/modules";
import {useUserRole} from "../../hooks/useUserRole";
import {TABLET_VERSION} from "../../constants";
import helper from "../../constants/helper";
import {setActiveTV, setListTV} from "../../store/activeTV/activeTVSlice";
import {useOutletContext} from "react-router-dom";
import {LayoutOutletContext} from "../PageOutlet/PageOutlet";

const PAGE_NAME = "ADULT";

const Adults = () => {
    const {t} = useTranslation("translations");
    const {innerWidth} = useResize();
    const dispatch = useDispatch();
    const role = useUserRole();

    const isMobile = innerWidth < TABLET_VERSION;
    const PROFILE_ID = useMemo(() => Number(localStorage.getItem(helper.EPIC_PROFILE_ID)), []);

    const [code, setCode] = useState<string>("");
    const [validate, setValidate] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [data, setData] = useState<IDataResponse[]>([]);
    const [isFetchingContent, setIsFetchingContent] = useState(false);
    const [selectedCategoryTab, setSelectedCategoryTab] = useState<number>(0);

    const {data: list, isFetching: isPageFetching} = useFetchCategoriesRequestQuery({
        page: PAGE_NAME,
        type: "category_list",
        role
    });
    const [fetchRelatedContent] = useFetchRelatedContentMutation();
    const [fetchContent] = useLazyGetPageContentQuery();

    const {setLayoutOptions, resetLayoutOptions} = useOutletContext<LayoutOutletContext>();

    const isWithoutMenu = Boolean(isMobile);

    useEffect(() => {
        setLayoutOptions({withoutMenu: isWithoutMenu});
        return () => resetLayoutOptions();
    }, [isWithoutMenu, setLayoutOptions, resetLayoutOptions]);

    useEffect(() => {
        const validated = sessionStorage.getItem("adultsPageValidated");
        if (validated === "true") {
            setValidate(true);
        }
    }, []);

    // Handle PIN verification
    const handleVerification = useCallback(async () => {
        try {
            await fetchRelatedContent({
                params: {profileID: String(PROFILE_ID)},
                body: {password: code}
            }).unwrap();
            setValidate(true);
            sessionStorage.setItem("adultsPageValidated", "true");
        } catch (err) {
            setError("Incorrect password");
        }
    }, [code, PROFILE_ID, fetchRelatedContent]);

    useEffect(() => {
        if (code.length === 4 && PROFILE_ID) {
            handleVerification?.();
        }
    }, [code, PROFILE_ID, handleVerification]);

    // Fetch all content
    const fetchAllContent = useCallback(
        async (isRefresh = false) => {
            if (!list?.length) return;

            if (!isRefresh) setIsFetchingContent(true);

            try {
                const results = await Promise.all(
                    list.map((item: any) =>
                        fetchContent({
                            page: PAGE_NAME,
                            role,
                            content_id: item.id,
                            profile_id: PROFILE_ID
                        }).unwrap()
                    )
                );

                const flattened = results.map((r) => r?.results ?? []).flat();
                setData(flattened);
                dispatch(setListTV(flattened as any));
                dispatch(setActiveTV(flattened[0] as any));
            } catch (e) {
                console.error("ADULT all fetch error:", e);
            } finally {
                if (!isRefresh) setIsFetchingContent(false);
            }
        },
        [list, role, PROFILE_ID, fetchContent]
    );

    useEffect(() => {
        if (validate) {
            fetchAllContent();
        }
    }, [validate, fetchAllContent]);

    // Handle tab change
    const handleTabChange = useCallback(
        async (categoryId: number) => {
            setSelectedCategoryTab(categoryId);
            setIsFetchingContent(true);
            try {
                if (categoryId === 0) {
                    await fetchAllContent(true);
                    return;
                }
                const response = await fetchContent({
                    page: PAGE_NAME,
                    role,
                    content_id: categoryId,
                    profile_id: PROFILE_ID
                }).unwrap();

                const results = response?.results ?? [];
                setData(results);
                dispatch(setListTV(results as any));
                dispatch(setActiveTV(results[0] as any));
            } catch (e) {
                console.error("LIVE tab fetch error:", e);
            } finally {
                setIsFetchingContent(false);
            }
        },
        [fetchAllContent, fetchContent, role, PROFILE_ID, dispatch]
    );

    // Show PIN verification form
    if (!validate) {
        return (
            <div className={styles.formPin}>
                <Typography variant="subtitle1">
                    {t("Authorization.enterYourPin")}
                </Typography>
                <Typography variant="subtitle2">
                    {t("Authorization.enterYourPinByUsingTheNumber")}
                </Typography>
                <VerificationCode
                    value={code}
                    onChange={(value) => {
                        setCode(value);
                        setError("");
                    }}
                />
                <span className={styles.error}>{error}</span>
            </div>
        );
    }

    // Show loader while fetching
    if (isPageFetching || (isFetchingContent && data.length === 0)) {
        return <Loader className={styles.loader}/>;
    }

    return (
        <>
            {!isMobile && list ? (
                <Box className={styles.wrapper}>
                    <Typography variant="h4" className={styles.title}>
                        {t("Aside.adults")}
                    </Typography>
                    <Box mb={2}>
                        <CustomTabs list={list} categories setSelected={handleTabChange}/>
                    </Box>
                    <Stack direction="row" flexWrap="wrap" justifyContent="center" gap="1rem" mt={5}>
                        {data?.map((e, i) => (
                            <MovieCard
                                key={i}
                                data={e}
                                isProgram
                                directPlay
                                contentContext={{page: "ADULT", categoryId: selectedCategoryTab}}
                            />
                        ))}
                    </Stack>
                </Box>
            ) : (
                <Box className={clsx(isMobile && styles.contentMobile)}>
                    {list && (
                        <Box className={styles.wrapper}>
                            <Typography variant="h4" className={styles.title}>
                                {t("Aside.adults")}
                            </Typography>

                            <Box mb={2}>
                                <CustomTabs list={list} categories setSelected={handleTabChange}/>
                            </Box>

                            <Stack direction="row" flexWrap="wrap" justifyContent="center" gap="1rem" mt={5}>
                                {data?.map((e, i) => (
                                    <MovieCard
                                        key={i}
                                        data={e}
                                        isProgram
                                        directPlay
                                        contentContext={{page: "ADULT", categoryId: selectedCategoryTab}}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Box>
            )}
            {/*{!isMobile && list ? (*/}
            {/*    <Box>*/}
            {/*        <div*/}
            {/*            className={styles.content}*/}
            {/*            style={{*/}
            {/*                backgroundImage: !isPreviewVisible*/}
            {/*                    ? `url(${firstSection?.backdrop_path || ""})`*/}
            {/*                    : "none",*/}
            {/*                transition: "opacity 0.8s ease-out",*/}
            {/*                opacity: !isPreviewVisible ? 1 : 0,*/}
            {/*            }}*/}
            {/*        >*/}
            {/*            <Typography variant="h4" className={styles.title}>*/}
            {/*                {t("Aside.adults")}*/}
            {/*            </Typography>*/}

            {/*            {firstSection && (*/}
            {/*                <div*/}
            {/*                    style={{*/}
            {/*                        zIndex: 1000001,*/}
            {/*                        position: "relative",*/}
            {/*                        marginBottom: "20px",*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    <FilmDescription*/}
            {/*                        data={firstSection}*/}
            {/*                        dataPreview={slicedPreviewVideos}*/}
            {/*                    />*/}
            {/*                </div>*/}
            {/*            )}*/}

            {/*            {isPlay && (*/}
            {/*                <VideoPlayerPreview*/}
            {/*                    data={slicedPreviewVideos}*/}
            {/*                    currentSlideIndex={0}*/}
            {/*                />*/}
            {/*            )}*/}
            {/*        </div>*/}

            {/*        <Box className={styles.wrapper}>*/}
            {/*            <Box mb={2}>*/}
            {/*                <CustomTabs*/}
            {/*                    list={list}*/}
            {/*                    categories*/}
            {/*                    setSelected={handleTabChange}*/}
            {/*                />*/}
            {/*            </Box>*/}

            {/*            <Stack*/}
            {/*                direction="row"*/}
            {/*                flexWrap="wrap"*/}
            {/*                justifyContent="center"*/}
            {/*                gap="1rem"*/}
            {/*                mt={5}*/}
            {/*            >*/}
            {/*                {data?.map((e, i) => (*/}
            {/*                    <MovieCard key={i} data={e}/>*/}
            {/*                ))}*/}
            {/*            </Stack>*/}
            {/*        </Box>*/}
            {/*    </Box>*/}
            {/*) : (*/}
            {/*    <Box className={clsx(isMobile && styles.contentMobile)}>*/}
            {/*        <div*/}
            {/*            className={styles.content}*/}
            {/*            style={{*/}
            {/*                backgroundImage: !isPreviewVisible*/}
            {/*                    ? `url(${firstSection?.backdrop_path || ""})`*/}
            {/*                    : "none",*/}
            {/*                transition: "opacity 0.8s ease-out",*/}
            {/*                opacity: !isPreviewVisible ? 1 : 0,*/}
            {/*            }}*/}
            {/*        >*/}
            {/*            {firstSection && (*/}
            {/*                <div*/}
            {/*                    style={{*/}
            {/*                        zIndex: 1000001,*/}
            {/*                        position: "relative",*/}
            {/*                        marginBottom: "20px",*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    <FilmDescription*/}
            {/*                        data={firstSection}*/}
            {/*                        dataPreview={slicedPreviewVideos}*/}
            {/*                    />*/}
            {/*                </div>*/}
            {/*            )}*/}

            {/*           */}
            {/*        </div>*/}

            {/*        {list && (*/}
            {/*            <Box className={styles.wrapper}>*/}
            {/*                <Typography variant="h4" className={styles.title}>*/}
            {/*                    {t("Aside.adults")}*/}
            {/*                </Typography>*/}

            {/*                <Box mb={2}>*/}
            {/*                    <CustomTabs*/}
            {/*                        list={list}*/}
            {/*                        categories*/}
            {/*                        setSelected={handleTabChange}*/}
            {/*                    />*/}
            {/*                </Box>*/}

            {/*                <Stack*/}
            {/*                    direction="row"*/}
            {/*                    flexWrap="wrap"*/}
            {/*                    justifyContent="center"*/}
            {/*                    gap="1rem"*/}
            {/*                    mt={5}*/}
            {/*                >*/}
            {/*                    {data?.map((e, i) => (*/}
            {/*                        <MovieCard key={i} data={e}/>*/}
            {/*                    ))}*/}
            {/*                </Stack>*/}
            {/*            </Box>*/}
            {/*        )}*/}
            {/*    </Box>*/}
            {/*)}*/}
        </>
    );
};

export default Adults;
